import { useState, useEffect } from 'react';
import api from '@/api';
import { X, Mail, Send, CheckCircle2, Loader2 } from 'lucide-react';

export default function EmailGuideModal({
  projectId,
  onClose,
  title = 'Email Workflow Guide',
  description = 'Sends a secure link (not an attachment) to the workflow guide. Pick a saved contact or type any email.',
  endpoint,
  showEdition = true,
  successNoun = 'guide',
}) {
  const [contacts, setContacts] = useState([]);
  const [toEmail, setToEmail] = useState('');
  const [edition, setEdition] = useState('owner');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const sendEndpoint = endpoint || `/closeout/projects/${projectId}/email-guide`;

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/contacts`)
      .then(r => {
        const withEmail = (r.data || []).filter(c => c.email);
        setContacts(withEmail);
        if (withEmail[0]) setToEmail(withEmail[0].email);
      })
      .catch(() => {});
  }, [projectId]);

  const send = async () => {
    setError(null);
    if (!toEmail || !toEmail.includes('@')) {
      setError('Enter a valid recipient email.');
      return;
    }
    setSending(true);
    try {
      const payload = { to_email: toEmail.trim() };
      if (showEdition) payload.edition = edition;
      const r = await api.post(sendEndpoint, payload);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to send the ${successNoun}.`);
    } finally {
      setSending(false);
    }
  };

  const resultLink = result?.link || result?.url;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="email-guide-modal">
      <div className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-sm font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" /> {title}
          </h3>
          <button onClick={onClose} data-testid="email-guide-close" className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {result ? (
          <div className="p-6 text-center" data-testid="email-guide-success">
            <CheckCircle2 className="w-9 h-9 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-foreground font-medium">
              {result.sent ? `${successNoun.charAt(0).toUpperCase() + successNoun.slice(1)} sent` : 'Secure link created'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {result.sent
                ? `A secure link was emailed to ${result.to}.`
                : 'Email could not be delivered, but your secure link is ready to copy below.'}
            </p>
            {resultLink && (
              <a href={resultLink} target="_blank" rel="noopener noreferrer"
                data-testid="email-guide-link"
                className="inline-block mt-3 text-[11px] text-primary break-all hover:underline">
                {resultLink}
              </a>
            )}
            <button onClick={onClose} className="mt-5 px-4 py-2 text-xs text-muted-foreground hover:text-foreground">Close</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground">{description}</p>

            {contacts.length > 0 && (
              <div>
                <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Service Contacts</label>
                <select value={toEmail} onChange={e => setToEmail(e.target.value)}
                  data-testid="email-guide-contact-select"
                  className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/40">
                  {contacts.map(c => (
                    <option key={c.id} value={c.email}>{`${c.name} — ${c.email}`}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Recipient Email</label>
              <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
                data-testid="email-guide-email-input" placeholder="owner@company.com"
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40" />
            </div>

            {showEdition && (
              <div>
                <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Edition</label>
                <div className="flex gap-1 bg-muted/50 border border-border rounded-sm p-0.5">
                  {[{ k: 'owner', l: 'Owner Edition' }, { k: 'pm', l: 'PM Edition' }].map(e => (
                    <button key={e.k} onClick={() => setEdition(e.k)}
                      data-testid={`email-guide-edition-${e.k}`}
                      className={`flex-1 px-3 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                        edition === e.k ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80 border border-transparent'
                      }`}>
                      {e.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="text-xs text-rose-500" data-testid="email-guide-error">{error}</p>}

            <button onClick={send} disabled={sending} data-testid="email-guide-send-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground text-xs font-heading uppercase tracking-wider rounded-sm hover:brightness-110 transition-all disabled:opacity-50">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Sending...' : 'Send Secure Link'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
